import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { ArticleDataObject } from "@app/types/Cue";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { MyBTKeywordsDataList } from "@app/types/Page";
import iconForwardArrowBlack from "@assets/icon-forward-arrow-black.svg";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import { topMobileNav } from "@components/Header/constant";
import Header from "@components/Header/Header";
import HomepageMasthead from "@components/Header/HomepageMasthead";
import MetaTags from "@components/MetaTags/MetaTags";
import MyBTCarousel from "@components/MyBT/MyBTCarousel";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import SeeMore from "@components/SeeMore/SeeMore";
import HomeSkybox from "@components/Skybox/HomeSkybox";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useWindowSize } from "@hooks/useWindowSize";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { cn, gaEventTracker, GoogleAdsSlotFactory } from "@util/helpers";
import cx from "classnames";

import Asean from "./Asean/Asean";
import BreakingNews from "./BreakingNews/BreakingNews";
import CompaniesMarkets from "./CompaniesMarkets/CompaniesMarkets";
import Lifestyle from "./Lifestyle/Lifestyle";
import OpinionFeatures from "./OpinionFeatures/OpinionFeatures";
import Overview from "./Overview/Overview";
import Podcasts from "./Podcasts/Podcasts";
import Video from "./Video/Video";
import { myBTRecommendedKeywords } from "../MyBT/constants";

type HomeContentProps = {
  title: string;
  overview: ArticleDataObject[];
  breakingNews: ArticleDataObject[];
  companiesMarkets: ArticleDataObject[];
  opinionFeatures: ArticleDataObject[];
  verticals: ArticleDataObject[];
  lifestyle: ArticleDataObject[];
  lifestyleCarousel: ArticleDataObject[];
  podcasts: ArticleDataObject[];
  myBTData: MyBTKeywordsDataList;
};

export default function HomeContent({
  title,
  overview,
  breakingNews,
  companiesMarkets,
  opinionFeatures,
  verticals,
  lifestyle,
  lifestyleCarousel,
  podcasts,
  myBTData,
}: HomeContentProps): React.ReactElement {
  const [myBTVisible, setMyBTVisible] = useState(true);

  const { isScreenMD } = useWindowSize();
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: { key: "page", value: "home" },
    });

  const seeMore =
    OKTAUserInfo?.usertype === OKTAUserTypeEnum.REGISTERED ||
    OKTAUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER
      ? "/mybt/onboard"
      : "/mybt/intro";
  const handleSeeMore: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    const target = e.target as HTMLAnchorElement;
    const label = target.href ? `see more - ${target.href}` : "see more";
    gaEventTracker("mybt", "click", label);
  };
  const { ref: myBTRef, inView: myBTBlock } = useInView();

  if (myBTBlock && myBTVisible) {
    gaEventTracker("mybt", "visible", window.location.href);
    setMyBTVisible(false);
  }

  return (
    <div className="w-full">
      <MetaTags
        title={title}
        description="THE BUSINESS TIMES - Find latest business & financial news including analysis and opinion on top business stories, stock markets in Singapore, Asia-Pacific & global market news and more at The Business Times."
        ogType="website"
        articlePageclass="frontpage"
        slug="/"
        keywords={[
          "singapore budget 2015",
          "singapore",
          "budget 2015",
          "business & financial news",
        ]}
      />

      <Advertisement
        rootClassName="min-h-[90px] bg-gray-125 py-1 md:py-6 border-y border-gray-175"
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1("homepage"),
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial("homepage")}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header isFromHome masthead={<HomepageMasthead />} />

      <GAData title="BT_Homepage" level2="home" />

      <Container rootClassName="pt-7 text-gray-850">
        <Row>
          <Column rootClassName="mb-1 mx-auto lg:hidden ">
            {/* <StockTicker /> */}
            <div className="flex gap-4">
              {topMobileNav.map((x) => {
                return (
                  <div className="group" key={x.key}>
                    <Link
                      key={x.key}
                      role="link"
                      to={`${x.link}?ref=home-menubar`}
                      className={cn(
                        "font-poppins text-lg font-medium text-gray-515",
                        {
                          "font-semibold text-gray-850": x.key === "home",
                        }
                      )}
                      reloadDocument
                    >
                      {x.homeLabel}
                    </Link>
                    <div
                      className={cx(
                        "mx-auto mt-1 h-[2px] w-[3rem] bg-gray-850 transition-all duration-300",
                        x.key === "home" ? "opacity-100" : "opacity-0",
                        "group-hover:opacity-100"
                      )}
                    ></div>
                  </div>
                );
              })}
            </div>
          </Column>

          <Column rootClassName="w-full mb-10">
            <Overview data={overview} />
          </Column>

          <Column rootClassName="w-full mb-10">
            <HomeSkybox />
          </Column>

          <>
            {breakingNews.length > 0 ? (
              <Column rootClassName="w-full mb-10">
                <BreakingNews
                  data={breakingNews}
                  rootClassName="border-t border-black pt-3"
                />
              </Column>
            ) : null}
          </>

          <Column rootClassName="w-full mb-10">
            <div ref={myBTRef}>
              <MyBTCarousel
                results={myBTData}
                keywords={myBTRecommendedKeywords}
                title="INTRODUCING"
                page="home"
                showTooltip={false}
                rootClassName="border-t pt-3 border-black"
                disablePaywall={
                  OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                }
              />
              {/* Todo add hover effect. */}
              {isScreenMD ? (
                <SeeMore
                  rootClassName="mt-4"
                  text="SEE MORE"
                  link={seeMore}
                  onClick={handleSeeMore}
                />
              ) : (
                <Link
                  to={seeMore}
                  className="btn d-md-none fw-700 rounded-pill mx-auto mt-4 block border border-[var(--digital-black)] px-4 py-2 text-center font-poppins font-bold text-black lg:inline-block"
                  target="_blank"
                  reloadDocument
                  onClick={handleSeeMore}
                >
                  DISCOVER MYBT
                  <img
                    src={iconForwardArrowBlack}
                    alt=""
                    width="8"
                    height="13"
                    className="mb-1 ml-2 inline"
                  />
                </Link>
              )}
            </div>
          </Column>

          <Column rootClassName="w-full lg:pr-6 lg:w-8/12 mb-10 lg:border-r border-gray-175 ">
            <CompaniesMarkets data={companiesMarkets} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-10">
            <iframe
              className="w-full lg:pl-3"
              src="https://sph.shareinvestor.com/bt_stock_chart_frame_2.html"
              title="BT Stock Chart"
              loading="lazy"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              width="100%"
              height={537}
            ></iframe>
          </Column>

          <Column rootClassName="w-full mb-10">
            <OpinionFeatures
              data={opinionFeatures}
              rootClassName={"border-t border-black pt-3"}
            />
          </Column>
        </Row>
      </Container>

      <div className="mb-10 w-full bg-gray-850 py-10">
        <Container>
          <Asean data={verticals} />
        </Container>
      </div>

      <Container>
        <Row>
          <Column rootClassName="w-full lg:pr-6 lg:w-8/12 mb-10 lg:border-r border-gray-175">
            <Podcasts data={podcasts} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-10 lg:pl-6">
            <Video />
          </Column>

          <Column rootClassName="w-full">
            <HomepageCarouselNewsletter rootClassName="border-t border-black pt-3 mb-6" />
          </Column>
        </Row>
      </Container>

      <div className="mb-10 w-full bg-gray-850 py-10">
        <Container>
          <Lifestyle data={lifestyle} carouselData={lifestyleCarousel} />
        </Container>
      </div>

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.CATFISH,
          slot: GoogleAdsSlotFactory.catfish("homepage"),
        }}
      />

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.ABM,
          slot: GoogleAdsSlotFactory.abm(),
        }}
      />

      <FooterSubscribeRow />
    </div>
  );
}
